'use client'

import { CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight'
import { BookmarkIcon } from '@phosphor-icons/react/dist/ssr/Bookmark'
import { PackageIcon } from '@phosphor-icons/react/dist/ssr/Package'
import { TagSimpleIcon } from '@phosphor-icons/react/dist/ssr/TagSimple'
import { TreeViewIcon } from '@phosphor-icons/react/dist/ssr/TreeView'
import { cx } from 'class-variance-authority'
import { ReviewType } from '@skop-app/lib-types'
import { useTranslations } from 'next-intl'

import { useActiveGrid } from '@/components/Grid'
import { useRouter } from '@/navigation'
import { useReview } from '@/context'
import type { ReviewDrawerElement, ReviewDrawerThread } from '@/types'
import { isSelectedReview } from '@/utils/reviews'

import { ReviewCard } from '../Review'
import { ReviewStatusBadge } from '../StatusBadge'

type ElementCardProps = {
  element: ReviewDrawerElement
  selectedReviewId?: string
  onSelectThread: (thread: ReviewDrawerThread) => void
  setReviewRef?: (reviewId: string) => (el: HTMLDivElement | null) => void
}

const ELEMENT_ICONS = {
  [ReviewType.REVIEW_OPTION]: TreeViewIcon,
  [ReviewType.REVIEW_PRODUCT]: PackageIcon,
  [ReviewType.REVIEW_REFERENCE]: TagSimpleIcon,
  [ReviewType.REVIEW_HEADER]: BookmarkIcon,
}

export function ElementCard({
  element,
  onSelectThread,
  selectedReviewId,
  setReviewRef,
}: ElementCardProps) {
  const t = useTranslations()
  const router = useRouter()
  const activeGrid = useActiveGrid()
  const gridRef = activeGrid?.gridRef
  const gridIsReady = activeGrid?.gridIsReady ?? false
  const { selectedReviews } = useReview()

  const { type, title, subtitle, threads } = element
  const firstThread = threads[0]
  const projectID = firstThread?.projectID
  const propertyID = firstThread?.fieldOptionsId

  const isSelected =
    threads.some((thread) => thread.reviewId === selectedReviewId) ||
    isSelectedReview(element, selectedReviews)

  const canRedirectToElement =
    type === ReviewType.REVIEW_PRODUCT ||
    type === ReviewType.REVIEW_REFERENCE ||
    (type === ReviewType.REVIEW_OPTION && Boolean(propertyID)) ||
    (type === ReviewType.REVIEW_HEADER && Boolean(projectID))

  const canLocateElement = gridIsReady || canRedirectToElement

  const handleElementLocate = () => {
    if (!canLocateElement) return

    const api = gridIsReady ? gridRef?.current?.api : undefined
    const rowNode = api?.getRowNode(element.id)

    if (rowNode) {
      api?.ensureNodeVisible(rowNode)
      api?.deselectAll()
      api?.flashCells({
        flashDuration: 1000,
        rowNodes: [rowNode],
      })
      rowNode.setSelected(true)
      return
    }

    switch (type) {
      case ReviewType.REVIEW_PRODUCT:
        if (projectID) {
          router.push(`/projects/ressources/${projectID}?node=${element.id}`)
          return
        }
        router.push(`/inventory?node=${element.id}`)
        return
      case ReviewType.REVIEW_OPTION:
        if (propertyID) {
          router.push(`/references/options/${propertyID}?node=${element.id}`)
        }
        return
      case ReviewType.REVIEW_REFERENCE:
        router.push(`/references?node=${element.id}`)
        return
      case ReviewType.REVIEW_HEADER:
        if (projectID) {
          router.push(`/projects/catalog/${projectID}#${element.id}`)
        }
        return
      default:
        return
    }
  }

  const Icon = ELEMENT_ICONS[type] || PackageIcon

  return (
    <section
      className={cx(
        'flex h-min w-full flex-col rounded-md border border-neutral-200 bg-neutral-50',
        isSelected && 'border-sky-100',
      )}
    >
      <div className="w-full px-4 pt-4 pb-2">
        <button
          type="button"
          className="block w-full min-w-0 text-left"
          onClick={handleElementLocate}
          disabled={!canLocateElement}
          aria-label={t('Review.openInGrid')}
        >
          <div className="flex w-full min-w-0 items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div
                className={cx(
                  'text-slate-500 hover:text-slate-700',
                  isSelected && 'font-bold text-slate-700',
                )}
              >
                <span className="block truncate text-base">
                  {title || 'Untitled'}
                </span>
              </div>
              <div className="mt-1 flex min-w-0 items-center gap-1.5 text-slate-400">
                <Icon className="size-3 shrink-0" />
                {subtitle ? (
                  <span className="truncate text-sm italic">{subtitle}</span>
                ) : null}
              </div>
            </div>
            <CaretRightIcon
              className={cx(
                'size-4.5 shrink-0',
                canLocateElement
                  ? 'text-slate-400'
                  : 'text-slate-300 opacity-50',
              )}
              aria-hidden
            />
          </div>
        </button>
      </div>

      <div className="flex w-full flex-col gap-0">
        {threads.map((thread) => {
          const isActiveThread = selectedReviewId === thread.reviewId

          return (
            <div
              key={thread.reviewId}
              ref={setReviewRef?.(thread.reviewId)}
              className="w-full"
            >
              {threads.length > 1 && (
                <button
                  type="button"
                  onClick={() => onSelectThread(thread)}
                  className="px-4 pb-2 pt-2 text-left"
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-medium text-slate-700">
                      {thread.displayLabel}
                    </span>
                    {thread.mentionsCount > 0 && (
                      <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                        {t('Review.mentionsCount', {
                          count: thread.mentionsCount,
                        })}
                      </span>
                    )}
                    {thread.status && (
                      <ReviewStatusBadge
                        status={thread.status}
                        size="compact"
                      />
                    )}
                  </div>
                </button>
              )}

              <ReviewCard
                thread={thread}
                isSelected={isActiveThread}
                onSelect={() => onSelectThread(thread)}
              />
            </div>
          )
        })}
      </div>
    </section>
  )
}
